import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useRef, useState } from "react";
import { AgentSessionType } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { LIGHT_GREY } from "styles/oneOffColor";

import Markdown from "components/Markdown";
import TypingLoader from "features/Home/Sidebar/ChatHelper/TypingLoader";
import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { Message } from "features/Influencer/HomePage/CreateTemplateDialog/Steps/ConversationalTemplate/schema";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface Props {
  agentSessionId: number | null;
  contentType: TemplateContentType;
  handleBack: () => void;
  handleNext: () => void;
}

const BentoAiFlowChat = ({
  agentSessionId,
  contentType,
  handleNext,
  handleBack,
}: Props) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);

  const [loadingMessage, setLoadingMessage] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);

  const [hasTemplate, setHasTemplate] = useState(false);
  const { setEditTemplateDialogId } = useContext(OutreachTemplatesContext);

  const handleCreateTemplate = async () => {
    setLoading(true);
    try {
      const { outreach, followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/template`,
        "POST",
      );
      if (outreach && outreach?.id) {
        setEditTemplateDialogId(outreach.id);
      } else if (followup && followup?.id) {
        setEditTemplateDialogId(followup.id);
      }
      handleNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const { messages } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}`,
        "GET",
      );
      setMessages(messages);
      if (messages?.length === 0) {
        setLoading(true);
        setLoadingMessage("Responding...");
        handleSubmitUserInput("Generate my template");
      } else {
        setLoading(false);
        const hasGeneratedTemplate = messages.some(
          (msg: Message) => msg.is_template,
        );
        setHasTemplate(hasGeneratedTemplate);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleSubmitUserInput = async (input: string) => {
    if (!input.trim()) return;
    if (input !== "Generate my template") {
      setMessages((prev) => [...prev, { role: "user", content: input }]);
    }

    setLoading(true);
    try {
      const { items } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/run`,
        "POST",
        {},
        {
          userInput: input,
          type: AgentSessionType.template,
          templateContentType: contentType,
        },
      );
      setMessages(items);
      setInput("");
      const lastItem = items[items.length - 1];
      displayAssistantMessage(lastItem.content);
      if (lastItem?.is_template) {
        setHasTemplate(true);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const displayAssistantMessage = (text: string) => {
    if (!text) return;
    setTyping(true);
    let index = 0;

    const interval = setInterval(() => {
      setMessages((prev) => {
        const updated = [...prev];
        const assistantMsg = { ...updated[updated.length - 1] };

        assistantMsg.content = text.slice(0, index + 1);
        updated[updated.length - 1] = assistantMsg;

        return updated;
      });

      index++;
      if (index >= text.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 10);
  };

  useEffect(() => {
    if (agentSessionId) {
      fetchMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, typing]);

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Create a Template with Bento's AI
      </Typography>
      <Typography gutterBottom>
        AI generates a first draft. Most creators tweak tone + add details
        before sending.
      </Typography>

      <Divider sx={{ backgroundColor: "purple.light", my: 2 }} />

      <Box>
        <Box flexGrow={1} overflow="auto" mb={2}>
          {messages.map((msg, i) => (
            <Box>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent:
                    msg.role === "user" ? "flex-end" : "flex-start",
                }}
                key={i}
              >
                <Paper
                  elevation={0}
                  sx={{
                    px: msg.role === "user" ? 2 : 0,
                    py: 0.75,
                    my: 1,
                    bgcolor:
                      msg.role === "user" ? "primary.main" : "transparent",
                    whiteSpace: "pre-wrap",
                    borderRadius: "16px",
                  }}
                >
                  <Markdown
                    content={msg.content}
                    color={msg.role === "user" ? "white" : "inherit"}
                  />
                </Paper>
              </Box>
            </Box>
          ))}

          {loading && (
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              ml={1}
            >
              <Paper
                elevation={0}
                sx={{
                  py: 0.75,
                  my: 1,
                  bgcolor: "transparent",
                  whiteSpace: "pre-wrap",
                }}
              >
                <TypingLoader title={loadingMessage || "Responding..."} />
              </Paper>
            </Typography>
          )}

          <div ref={chatEndRef} />
        </Box>

        <Box
          component="form"
          onSubmit={(e: CustomEvent) => {
            e.preventDefault();
            handleSubmitUserInput(input);
          }}
          display="flex"
          alignItems={"center"}
          gap={1}
          sx={{
            pr: 3,
            border: "1px black solid",
            borderRadius: 7,
            overflow: "hidden",
          }}
        >
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            fullWidth
            placeholder={"Type your message here..."}
            disabled={loading || typing}
            multiline
            minRows={1}
            maxRows={10}
            onKeyDown={(e: CustomEvent) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmitUserInput(e.target.value);
              }
            }}
            sx={styles.textField}
          />
          {loading || typing ? (
            <CircularProgress size={20} />
          ) : (
            <IconButton
              type="submit"
              disabled={loading || typing}
              sx={styles.submitButton}
            >
              <Box component="i" className="fa-regular fa-circle-arrow-up" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container sx={{ py: 3 }} justifyContent={"center"} gap={1}>
        <Button
          sx={{ backgroundColor: LIGHT_GREY, px: 3, py: 1 }}
          onClick={handleBack}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-arrow-left"
            sx={{ mr: 1 }}
          />{" "}
          Back
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!hasTemplate || typing || loading}
          onClick={handleCreateTemplate}
          sx={{ px: 6, py: 1 }}
        >
          Next{" "}
        </Button>
      </Grid>
    </>
  );
};

export default BentoAiFlowChat;
